/**
 * KnowledgeBaseService
 *
 * Handles CRUD operations for knowledge base articles and feature suggestions.
 */

import { supabase } from '@/lib/supabase';

export class KnowledgeBaseService {
  /**
   * Get all knowledge base articles with optional filters
   */
  static async getArticles({ category = null, search = null, limit = 50 } = {}) {
    let query = supabase
      .from('kb_articles')
      .select(`
        *,
        created_by_user:created_by(id, email, raw_user_meta_data),
        updated_by_user:updated_by(id, email, raw_user_meta_data)
      `)
      .eq('status', 'published')
      .order('view_count', { ascending: false })
      .limit(limit);

    if (category) {
      query = query.eq('category', category);
    }

    if (search) {
      query = query.or(`title.ilike.%${search}%,content.ilike.%${search}%,tags.cs.{${search}}`);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  }

  /**
   * Get a single article by slug or ID
   */
  static async getArticle(slugOrId) {
    const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(slugOrId);

    const { data, error } = await supabase
      .from('kb_articles')
      .select(`
        *,
        created_by_user:created_by(id, email, raw_user_meta_data),
        updated_by_user:updated_by(id, email, raw_user_meta_data)
      `)
      .eq(isUUID ? 'id' : 'slug', slugOrId)
      .single();

    if (error) throw error;

    // Increment view count
    if (data) {
      await supabase
        .from('kb_articles')
        .update({ view_count: (data.view_count || 0) + 1 })
        .eq('id', data.id);
    }

    return data;
  }

  /**
   * Get categories with article counts
   */
  static async getCategoriesWithCounts() {
    const { data, error } = await supabase
      .from('kb_articles')
      .select('category')
      .eq('status', 'published');

    if (error) throw error;

    const counts = {};
    (data || []).forEach(article => {
      counts[article.category] = (counts[article.category] || 0) + 1;
    });

    return counts;
  }

  /**
   * Get popular/trending articles
   */
  static async getPopularArticles(limit = 5) {
    const { data, error } = await supabase
      .from('kb_articles')
      .select('id, title, slug, category, view_count, helpful_count')
      .eq('status', 'published')
      .order('view_count', { ascending: false })
      .limit(limit);

    if (error) throw error;
    return data || [];
  }

  /**
   * Mark article as helpful
   */
  static async markHelpful(articleId, userId) {
    // Check if already marked
    const { data: existing } = await supabase
      .from('kb_article_feedback')
      .select('id')
      .eq('article_id', articleId)
      .eq('user_id', userId)
      .single();

    if (existing) return false; // Already marked

    // Add feedback
    await supabase
      .from('kb_article_feedback')
      .insert({ article_id: articleId, user_id: userId, helpful: true });

    // Increment count
    const { data: article } = await supabase
      .from('kb_articles')
      .select('helpful_count')
      .eq('id', articleId)
      .single();

    await supabase
      .from('kb_articles')
      .update({ helpful_count: (article?.helpful_count || 0) + 1 })
      .eq('id', articleId);

    return true;
  }

  /**
   * Search articles with AI-powered relevance
   */
  static async searchArticles(query) {
    // For now, use simple text search
    // TODO: Integrate with AI for semantic search
    const { data, error } = await supabase
      .from('kb_articles')
      .select('id, title, slug, category, excerpt, tags')
      .eq('status', 'published')
      .or(`title.ilike.%${query}%,content.ilike.%${query}%,excerpt.ilike.%${query}%`)
      .limit(10);

    if (error) throw error;
    return data || [];
  }

  // ==================== Feature Suggestions ====================

  /**
   * Submit a new feature suggestion
   */
  static async submitSuggestion({ title, description, type, userId, userEmail, area = null }) {
    const { data, error } = await supabase
      .from('kb_feature_suggestions')
      .insert({
        title,
        description,
        suggestion_type: type,
        area,
        submitted_by: userId,
        submitter_email: userEmail,
        status: 'submitted',
        votes: 1 // Auto-vote by submitter
      })
      .select()
      .single();

    if (error) throw error;

    // Also vote for own suggestion
    if (data) {
      await supabase
        .from('kb_suggestion_votes')
        .insert({ suggestion_id: data.id, user_id: userId });
    }

    return data;
  }

  /**
   * Get all feature suggestions
   */
  static async getSuggestions({ status = null, type = null, limit = 50 } = {}) {
    let query = supabase
      .from('kb_feature_suggestions')
      .select(`
        *,
        submitter:submitted_by(id, email, raw_user_meta_data)
      `)
      .order('votes', { ascending: false })
      .limit(limit);

    if (status) {
      query = query.eq('status', status);
    }

    if (type) {
      query = query.eq('suggestion_type', type);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  }

  /**
   * Vote for a suggestion
   */
  static async voteSuggestion(suggestionId, userId) {
    // Check if already voted
    const { data: existing } = await supabase
      .from('kb_suggestion_votes')
      .select('id')
      .eq('suggestion_id', suggestionId)
      .eq('user_id', userId)
      .single();

    if (existing) return false; // Already voted

    // Add vote
    await supabase
      .from('kb_suggestion_votes')
      .insert({ suggestion_id: suggestionId, user_id: userId });

    // Increment vote count
    const { data: suggestion } = await supabase
      .from('kb_feature_suggestions')
      .select('votes')
      .eq('id', suggestionId)
      .single();

    await supabase
      .from('kb_feature_suggestions')
      .update({ votes: (suggestion?.votes || 0) + 1 })
      .eq('id', suggestionId);

    return true;
  }

  /**
   * Get user's vote status for suggestions
   */
  static async getUserVotes(userId, suggestionIds) {
    if (!suggestionIds?.length) return {};

    const { data, error } = await supabase
      .from('kb_suggestion_votes')
      .select('suggestion_id')
      .eq('user_id', userId)
      .in('suggestion_id', suggestionIds);

    if (error) throw error;

    const votes = {};
    (data || []).forEach(v => {
      votes[v.suggestion_id] = true;
    });

    return votes;
  }

  /**
   * Add comment to a suggestion
   */
  static async addSuggestionComment(suggestionId, userId, comment) {
    const { data, error } = await supabase
      .from('kb_suggestion_comments')
      .insert({
        suggestion_id: suggestionId,
        user_id: userId,
        comment
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  /**
   * Update suggestion status (admin only)
   */
  static async updateSuggestionStatus(suggestionId, status, adminNote = null) {
    const updateData = { status, updated_at: new Date().toISOString() };
    if (adminNote) {
      updateData.admin_note = adminNote;
    }

    const { data, error } = await supabase
      .from('kb_feature_suggestions')
      .update(updateData)
      .eq('id', suggestionId)
      .select()
      .single();

    if (error) throw error;
    return data;
  }
}

export default KnowledgeBaseService;
